"use client";

import { motion } from "motion/react";
import { Award, Link2 } from "lucide-react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";

import { usePublicProfileStore } from "../store/usePublicProfileStore";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";

export function PublicCertificatesDisplay() {
  const { profileData } = usePublicProfileStore();
  const data = profileData?.certificates_section;
  const { t } = useTranslation(["profile"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });

  if (!data || data.length === 0) return null;

  return (
    <Card className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-l-4 border-none border-l-purple-400/50 shadow-2xl backdrop-blur-xl">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -right-10 -bottom-15 h-32 w-32 rounded-full bg-gradient-to-br from-purple-400 to-violet-600" />
        <div className="absolute -top-10 -left-10 h-28 w-32 rounded-full bg-gradient-to-tr from-violet-400 to-purple-500" />
      </div>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <motion.h2
          animate={{ y: 0, opacity: 1 }}
          className="text-accent-foreground mb-2 flex items-center gap-2 text-xl font-semibold"
          initial={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-center rounded-xl bg-purple-500/20 p-2">
            <Icon
              className="text-purple-400"
              height="1.5rem"
              icon="mdi:scroll-text-outline"
              width="1.5rem"
            />
          </div>
          {t("profile:certificatesSection.title")}
        </motion.h2>
      </CardHeader>
      <CardContent className="space-y-6">
        {data.map((certificate, index) => (
          <motion.div
            key={certificate.id}
            animate={{ y: 0, opacity: 1 }}
            className="space-y-2 rounded-2xl border border-purple-300/20 bg-purple-500/10 p-4 backdrop-blur-sm transition-colors hover:bg-purple-500/15"
            initial={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <div className="flex items-start space-x-4">
              <div className="mt-1 flex items-center justify-center rounded-xl bg-purple-500/20 p-2">
                <Icon
                  className="text-purple-400"
                  height="1.5rem"
                  icon="mdi:certificate-outline"
                  width="1.5rem"
                />
              </div>
              <div className="ml-2! space-y-1">
                <h3 className="text-accent-foreground text-lg font-semibold">{certificate.name}</h3>
                <p className="inline-flex items-center gap-2 rounded-xl border border-purple-400/30 bg-purple-500/20 px-3 py-2">
                  <Award className="h-4 w-4 text-purple-300" />
                  <span className="text-sm font-medium text-purple-300">
                    {certificate.issuing_organization}
                  </span>
                </p>
                {certificate.issue_date && (
                  <p className="text-muted-foreground flex items-center text-sm">
                    <Icon
                      className="mr-2"
                      height="1rem"
                      icon="mdi:calendar-edit-outline"
                      width="1rem"
                    />
                    {certificate.issue_date}
                    {certificate.expiration_date && (
                      <>
                        <Icon
                          className="ms-2 mr-2"
                          height="1rem"
                          icon="mdi:calendar-remove-outline"
                          width="1rem"
                        />
                        {certificate.expiration_date}
                      </>
                    )}
                  </p>
                )}
                {certificate.credential_id && (
                  <div className="rounded-xl bg-purple-500/5 p-3">
                    <p className="text-accent-foreground font-mono text-sm">
                      {t("profile:certificatesSection.display.credentialId")}
                    </p>
                    <p className="text-sm">{certificate.credential_id}</p>
                  </div>
                )}
                {certificate.description && (
                  <div className="rounded-xl bg-purple-500/5 p-3">
                    <p className="text-muted-foreground text-xs">
                      {t("profile:certificatesSection.display.description")}
                    </p>
                    <p className="text-secondary-foreground text-sm">{certificate.description}</p>
                  </div>
                )}
                {certificate.credential_url && (
                  <a
                    className="mt-3 inline-flex items-center gap-2 rounded-xl border border-purple-400/30 bg-purple-500/20 px-3 py-2 text-sm font-medium text-purple-300 transition-colors hover:text-purple-200"
                    href={certificate.credential_url}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <Link2 className="h-4 w-4" />
                    {t("profile:certificatesSection.display.viewCredential")}
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </CardContent>
    </Card>
  );
}
